import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Divider, Tag, Tooltip } from "antd";
import type { AllBuildingBlocks, VisibilityMap } from "../types";
import { isVisibleForPeriod } from "../types";
import { BUILDING_BLOCK_LABELS } from "../schema";
import { getBlockDateLabel } from "../timeframe";
import { ObjectivesBBTab, OBJECTIVES_BB_CONFIGS } from "./ObjectivesBBTab";
import { ProseBBTab, PROSE_BB_CONFIGS } from "./ProseBBTab";

import {
  Eye,
  Target,
  Mountain,
  Layers,
  Calendar,
  CalendarDays,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Pencil,
  Lock,
  Unlock
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const blockIcons: Record<string, React.ElementType> = {
  vision: Eye,
  mission: Target,
  peakAmbition: Mountain,
  peakAmbitionObjectives: Layers,
  threeYearObjectives: CalendarDays,
  oneYearObjectives: Calendar,
  quarterlyObjectives: CalendarClock
};

const blockColors: Record<string, string> = {
  vision: "bg-[#f37037]/10 text-[#f37037]",
  mission: "bg-[#2c1561]/10 text-[#2c1561]",
  peakAmbition: "bg-[#802a88]/10 text-[#802a88]",
  peakAmbitionObjectives: "bg-[#4b1d70]/10 text-[#4b1d70]",
  threeYearObjectives: "bg-[#9b1561]/10 text-[#9b1561]",
  oneYearObjectives: "bg-[#ef4269]/10 text-[#ef4269]",
  quarterlyObjectives: "bg-[#ebf2fa] text-[#2c1561]"
};

function StatusBadge({ status }: { status: string }) {
  if (status === "complete") {
    return (
      <Tag
        icon={<CheckCircle2 className="h-3 w-3 text-emerald-500" />}
        color="success"
      >
        Complete
      </Tag>
    );
  }
  if (status === "partial") {
    return (
      <Tag
        icon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
        color="warning"
      >
        Partial
      </Tag>
    );
  }
  return <Tag>Empty</Tag>;
}

function getEffectiveStatus(
  block: any,
  isPeakAmbition: boolean,
  isObjectives: boolean
): string {
  if (!block) return "empty";
  const stored = block.status || "empty";
  if (stored === "complete" || stored === "partial") return stored;
  if (isPeakAmbition && (block.headline || block.draft)) return "partial";
  if (isObjectives && block.wmbt?.length > 0) return "partial";
  if (block.draft) return "partial";
  return "empty";
}

function getBlockPreview(
  block: any,
  isPeakAmbition: boolean,
  isObjectives: boolean
): string {
  if (!block) return "";
  if (isPeakAmbition) return block.headline || block.draft || "";
  if (isObjectives && block.wmbt?.length > 0) {
    const first = block.wmbt[0];
    // `description` is the research-integration alias for the objective text.
    return first.categoryObjective || first.label || first.objective || first.text || first.description || "";
  }
  return block.draft || "";
}

// ── RoadmapGrid ───────────────────────────────────────────────────────────────

interface RoadmapGridProps {
  buildingBlocks: AllBuildingBlocks;
  companyName: string;
  sessionId: string | number;
  peakDate?: string | null;
  yearStartMonth?: number;
  yearStartDay?: number;
  isAdvisorOrOwner?: boolean;
  currentUserId?: string;
  /** Viewer's session role — threaded to ProseBBTab so the team collaborative
   *  editor gates edit/comment correctly for the expanded grid-card path too
   *  (focus mode passes this via BlockDetailView; the grid must as well). */
  userRole?: string;
  onFocusBlock?: (blockKey: string, opts?: { autoEdit?: boolean }) => void;
  /** Re-fetch the session after a tab action (synthesis, lock, restore…). Must only
   *  reload state — never save, or team content gets written into the viewer's solo output. */
  onSessionRefresh?: () => void;
  lockedBlocks?: Record<string, boolean>;
  /** Current-calendar-period lock state for objectives BBs (indicator-only, not clickable) */
  periodLockedBlocks?: Record<
    string,
    { locked: boolean; periodKey: string; isActive: boolean }
  >;
  onToggleLock?: (blockKey: string, locked: boolean) => void;
  /** Solo per-block commit toggle (the solo mirror of onToggleLock; shown in Solo view). */
  onToggleCommit?: (blockKey: string, committed: boolean) => void;
  /** Global Solo/Team mode from the Roadmap panel toggle */
  viewMode?: "solo" | "team";
  /** Team-data visibility map — gates the collapsed team preview for contributors,
   *  mirroring the ProseBBTab expanded-view gate (both-or-neither). */
  visibilityMap?: VisibilityMap;
  onViewModeChange?: (v: "solo" | "team") => void;
  /** Building blocks from the linked session (team blocks when on solo, solo blocks when on parent) */
  linkedBlocks?: AllBuildingBlocks | null;
  /**
   * Per-viewer blocks (RoadmapOutput.viewerOutput.buildingBlocks). Used for the
   * collapsed-row preview and the row-level status so the grid surfaces what the
   * current user actually edited — PATCH /sessions/:id/blocks writes the
   * viewer's RoadmapOutput, not the team session document, so reading the team
   * slice here showed stale-empty content after any refresh. The expanded
   * ObjectivesBBTab / ProseBBTab tabs also receive this as `soloBuildingBlocks`
   * — their internal solo/team toggle reads viewerBlocks for solo and the
   * parent's buildingBlocks for team. Without this prop, solo-mode expanded
   * tabs render the empty team slice (see pre-mortem 2026-05-14 scope
   * extension, 2026-05-19).
   */
  viewerBlocks?: AllBuildingBlocks;
  /** Called when QO tab changes visibility, so parent can update session + studio */
  onVisibilityChange?: (updatedMap: VisibilityMap) => void;
  /** Called when a TAR is created or deleted so Studio can refresh */
  onReportCreated?: () => void;
  onSendToMainChat?: (message: string) => void;
  /**
   * State 2 gate (2+ participants). Forwarded to the ObjectivesBBTab /
   * ProseBBTab so their team-only command-bar controls stay hidden in
   * single-user (State 1) roadmaps where showViewToggle is false.
   */
  showViewToggle?: boolean;
}

export function RoadmapGrid({
  buildingBlocks,
  companyName,
  sessionId,
  peakDate,
  yearStartMonth = 1,
  yearStartDay = 1,
  isAdvisorOrOwner = false,
  currentUserId,
  userRole,
  onFocusBlock,
  onSessionRefresh,
  lockedBlocks = {},
  periodLockedBlocks: periodLockedBlocks = {},
  onToggleLock,
  onToggleCommit,
  viewMode,
  visibilityMap,
  onViewModeChange,
  linkedBlocks,
  viewerBlocks,
  onVisibilityChange,
  onReportCreated,
  onSendToMainChat,
  showViewToggle = false,
}: RoadmapGridProps) {
  const navigate = useNavigate();
  // When linkedBlocks is provided, use it for the display blocks (the toggle has switched context)
  const isShowingLinkedBlocks = !!linkedBlocks;
  // Preview / status source. Prefer linkedBlocks (explicit switch). Otherwise
  // follow the global view mode: Team reads consensus, Solo reads the viewer's
  // per-user slice (falling back to team for legacy data with no viewerOutput).
  const blocks =
    linkedBlocks ||
    (viewMode === "team" ? buildingBlocks : viewerBlocks || buildingBlocks) ||
    {};

  const blockKeys = [
    "vision",
    "mission",
    "peakAmbition",
    "peakAmbitionObjectives",
    "threeYearObjectives",
    "oneYearObjectives",
    "quarterlyObjectives"
  ] as const;

  const hasAnyContent = blockKeys.some((key) => {
    const block = (blocks as any)[key];
    return (
      block &&
      (block.draft || block.headline || (block.wmbt && block.wmbt.length > 0))
    );
  });

  const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>(
    () => Object.fromEntries(blockKeys.map((key) => [key, false]))
  );
  const allExpanded = Object.values(expandedBlocks).every(Boolean);

  const toggleAll = () => {
    setExpandedBlocks(
      Object.fromEntries(blockKeys.map((k) => [k, !allExpanded]))
    );
  };

  const toggleBlock = (key: string) => {
    setExpandedBlocks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Frozen roadmap header */}
      <div className="px-3 pt-3 pb-2 bg-white border-b border-slate-100 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2
              className="text-base font-semibold"
              data-testid="text-roadmap-title"
            >
              {companyName} Roadmap
            </h2>
            <p className="text-xs text-muted-foreground">
              {isShowingLinkedBlocks
                ? viewMode === "team"
                  ? "Viewing the team's roadmap. Switch to Solo to see your version."
                  : "Viewing your individual roadmap. Switch to Team to see the team version."
                : hasAnyContent
                  ? "Your strategic roadmap is taking shape. Review and refine each block."
                  : "Start chatting with Vo to build your roadmap."}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="text"
              size="small"
              onClick={() => navigate(`/roadmap/mountain/${sessionId}?view=${viewMode === "team" ? "team" : "solo"}`)}
              icon={<Mountain className="h-3.5 w-3.5" />}
              style={{ fontSize: 12, color: "#64748b" }}
              title="Mountain View"
            />
            {hasAnyContent && (
              <Button
                type="text"
                size="small"
                onClick={toggleAll}
                icon={
                  allExpanded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )
                }
                style={{ fontSize: 12, color: "#64748b" }}
              >
                {allExpanded ? "Collapse all" : "Expand all"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable blocks list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
        {blockKeys.map((key, idx) => {
          const meta = BUILDING_BLOCK_LABELS[key];
          const block = (blocks as any)[key] || {
            status: "empty",
            draft: "",
            gaps: [],
            sources: []
          };
          const Icon = blockIcons[key] || Layers;
          const colorClass = blockColors[key] || "";

          const isPeakAmbition = key === "peakAmbition";
          const isObjectives =
            key.includes("Objectives") || key === "peakAmbitionObjectives";
          const isExpanded = expandedBlocks[key] ?? false;
          const isLocked = !!lockedBlocks?.[key];
          const dateLabel = getBlockDateLabel(
            key,
            peakDate,
            yearStartMonth,
            yearStartDay
          );
          const effectiveStatus = getEffectiveStatus(
            block,
            isPeakAmbition,
            isObjectives
          );
          const preview = getBlockPreview(block, isPeakAmbition, isObjectives);
          // Gate the collapsed TEAM preview for contributors exactly like the
          // expanded ProseBBTab view does — hidden until the owner opens team
          // visibility for that block. Without this, team content leaked in the
          // collapsed card while the expanded view correctly hid it.
          const previewGatedOut =
            viewMode === "team" &&
            !isAdvisorOrOwner &&
            key in PROSE_BB_CONFIGS &&
            !isVisibleForPeriod(
              visibilityMap,
              PROSE_BB_CONFIGS[key].periodType,
              `bb-${PROSE_BB_CONFIGS[key].bbType}`
            );

          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card
                styles={{ body: { padding: 0 } }}
                data-testid={`card-block-${key}`}
              >
                {/* Sticky block header — stays visible while scrolling through expanded content */}
                <div
                  className={`sticky top-0 z-10 bg-white px-3 pt-3 pb-2 rounded-t-[8px] ${isExpanded ? "border-b border-slate-100" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div
                        className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${colorClass}`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground leading-none shrink-0">
                            {String(meta.number).padStart(2, "0")}
                          </span>
                          <span className="text-sm font-semibold leading-none truncate">
                            {meta.label}
                          </span>
                        </div>
                        {dateLabel && !(key in OBJECTIVES_BB_CONFIGS) ? (
                          <span className="text-[10px] text-primary/70 font-mono mt-0.5 ml-6">
                            {dateLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {!(key in OBJECTIVES_BB_CONFIGS) &&
                        !(key in PROSE_BB_CONFIGS) && (
                          <StatusBadge status={effectiveStatus} />
                        )}

                      {/* Focus button */}
                      {onFocusBlock && (
                        <Tooltip title="Focus on this block">
                          <Button
                            type="text"
                            size="small"
                            icon={<Maximize2 className="h-3.5 w-3.5" />}
                            onClick={() => onFocusBlock(key)}
                            style={{ padding: "0 4px", height: 24 }}
                            aria-label="Focus"
                          />
                        </Tooltip>
                      )}

                      {/* Objectives BBs: current-period lock indicator (lock/unlock happens inside the tab) */}
                      {isObjectives &&
                        (() => {
                          const info = periodLockedBlocks[key];
                          if (!info) return null;
                          const cfg = OBJECTIVES_BB_CONFIGS[key];
                          const periodLabel =
                            cfg && info.periodKey
                              ? cfg.periodLabelFn(info.periodKey)
                              : info.periodKey;
                          const activeSuffix = info.isActive ? " (Active)" : "";
                          return info.locked ? (
                            <Tooltip
                              title={`Current ${cfg?.periodNoun || "period"} ${periodLabel} is locked`}
                            >
                              <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={`Current ${cfg?.periodNoun || "period"} ${periodLabel} is unlocked${activeSuffix}`}
                            >
                              <Unlock className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                            </Tooltip>
                          );
                        })()}

                      {/* Solo view: per-block COMMIT toggle (locked = committed / unlocked = draft),
                          on EVERY block (commit is a uniform solo concept — unlike the team lock,
                          it is NOT excluded for prose/objectives). Anyone commits their own view. */}
                      {viewMode === "solo" &&
                        onToggleCommit &&
                        effectiveStatus !== "empty" && (() => {
                          const isCommitted =
                            (block as { commitState?: string })?.commitState === "committed";
                          return (
                            <Tooltip
                              title={
                                isCommitted
                                  ? "Committed — click to move back to draft"
                                  : "Draft — click to commit this block"
                              }
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={
                                  isCommitted ? (
                                    <Lock className="h-3.5 w-3.5 text-emerald-600" />
                                  ) : (
                                    <Unlock className="h-3.5 w-3.5 text-slate-300" />
                                  )
                                }
                                onClick={() => onToggleCommit(key, !isCommitted)}
                                style={{ padding: "0 4px", height: 24 }}
                                aria-label={isCommitted ? "Move to draft" : "Commit"}
                              />
                            </Tooltip>
                          );
                        })()}

                      {/* Team view: Lock toggle — O/A only (BB tabs handle their own locking) */}
                      {!isObjectives &&
                        !(key in PROSE_BB_CONFIGS) &&
                        viewMode !== "solo" &&
                        isAdvisorOrOwner &&
                        onToggleLock && (
                          <Tooltip
                            title={
                              isLocked
                                ? "Unlock this block"
                                : "Lock this block (read-only for all)"
                            }
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={
                                isLocked ? (
                                  <Lock className="h-3.5 w-3.5 text-amber-500" />
                                ) : (
                                  <Lock className="h-3.5 w-3.5 text-slate-300" />
                                )
                              }
                              onClick={() => onToggleLock(key, !isLocked)}
                              style={{ padding: "0 4px", height: 24 }}
                              aria-label={isLocked ? "Unlock" : "Lock"}
                            />
                          </Tooltip>
                        )}

                      {/* Non-objectives/non-prose: Lock indicator for non-O/A when block is locked */}
                      {!isObjectives &&
                        !(key in PROSE_BB_CONFIGS) &&
                        isLocked &&
                        !isAdvisorOrOwner && (
                          <Tooltip title="This block is locked">
                            <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          </Tooltip>
                        )}

                      {/* Edit button — opens focus mode with editing pre-enabled */}
                      {onFocusBlock &&
                        !isLocked &&
                        !isShowingLinkedBlocks &&
                        !(isObjectives && periodLockedBlocks[key]?.locked) && (
                          <Tooltip title="Edit (focus mode)">
                            <Button
                              type="text"
                              size="small"
                              icon={<Pencil className="h-3.5 w-3.5" />}
                              onClick={() =>
                                onFocusBlock(key, { autoEdit: true })
                              }
                              style={{ padding: "0 4px", height: 24 }}
                              aria-label="Edit in focus mode"
                            />
                          </Tooltip>
                        )}

                      <Button
                        type="text"
                        size="small"
                        onClick={() => toggleBlock(key)}
                        icon={
                          isExpanded ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )
                        }
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        style={{ padding: "0 4px", height: 24 }}
                      />
                    </div>
                  </div>

                  <p
                    id="building-block-description-in-grid"
                    className="text-[11px] italic text-muted-foreground/70 mt-1.5 leading-snug"
                  >
                    {meta.description}
                  </p>
                </div>

                {!isExpanded && preview && !previewGatedOut && (
                  <div className="px-3 pb-3">
                    <p
                      id="building-block-text-in-grid"
                      className="mt-2 text-xs text-foreground/60 line-clamp-1"
                    >
                      {preview.length > 120
                        ? preview.slice(0, 120) + "…"
                        : preview}
                    </p>
                  </div>
                )}

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      {key in OBJECTIVES_BB_CONFIGS ? (
                        <div className="max-h-[480px] overflow-y-auto border-t border-slate-100">
                          <ObjectivesBBTab
                            config={OBJECTIVES_BB_CONFIGS[key]}
                            sessionId={String(sessionId)}
                            buildingBlocks={buildingBlocks}
                            soloBuildingBlocks={viewerBlocks}
                            isAdvisorOrOwner={isAdvisorOrOwner}
                            currentUserId={currentUserId}
                            viewMode={viewMode}
                            showViewToggle={showViewToggle}
                            onViewModeChange={onViewModeChange}
                            onVisibilityChange={onVisibilityChange}
                            onReportCreated={onReportCreated}
                            onSendToMainChat={onSendToMainChat}
                            onSessionRefresh={onSessionRefresh}
                          />
                        </div>
                      ) : key in PROSE_BB_CONFIGS ? (
                        <div className="max-h-[480px] overflow-y-auto border-t border-slate-100">
                          <ProseBBTab
                            config={PROSE_BB_CONFIGS[key]}
                            sessionId={String(sessionId)}
                            buildingBlocks={buildingBlocks}
                            soloBuildingBlocks={viewerBlocks}
                            isAdvisorOrOwner={isAdvisorOrOwner}
                            currentUserId={currentUserId}
                            userRole={userRole}
                            viewMode={viewMode}
                            showViewToggle={showViewToggle}
                            onViewModeChange={onViewModeChange}
                            onVisibilityChange={onVisibilityChange}
                            onReportCreated={onReportCreated}
                            onSendToMainChat={onSendToMainChat}
                            onSessionRefresh={onSessionRefresh}
                          />
                        </div>
                      ) : (
                        <div className="px-3 pb-3">
                          <Divider style={{ margin: "8px 0 6px" }} />

                          {/* Empty state */}
                          {block.status === "empty" &&
                            !block.draft &&
                            !(isPeakAmbition && block.headline) && (
                              <div className="flex items-center justify-center py-3 border border-dashed rounded-md bg-muted/20">
                                <p className="text-xs text-muted-foreground">
                                  Chat with Vo to populate this block
                                </p>
                              </div>
                            )}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
